import React, { useEffect, useState } from "react";

export default function HealthGauge({ value = 21.8, size = 110 }) {
  const min = 10;
  const max = 40;

  const segments = [
    { from: 10, to: 18.5, color: "#3B82F6", label: "Underweight" },
    { from: 18.5, to: 25, color: "#22C55E", label: "Normal" },
    { from: 25, to: 30, color: "#F59E0B", label: "Overweight" },
    { from: 30, to: 40, color: "#EF4444", label: "Obese" },
  ];

  const [animatedValue, setAnimatedValue] = useState(min);
  const [activeSegment, setActiveSegment] = useState(null);

  /* ---------- CLAMP VALUE ---------- */
  const clamp = (val) => Math.max(min, Math.min(val, max));

  /* ---------- ANIMATION ---------- */
  useEffect(() => {
    const start = performance.now();
    const duration = 500;

    const target = clamp(value);

    const animate = (t) => {
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);

      setAnimatedValue(min + (target - min) * eased);

      if (p < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [value]);

  /* ---------- GEOMETRY ---------- */
  const width = size;
  const height = size / 2 + 10;
  const cx = width / 2;
  const cy = height;
  const r = width / 2 - 10;

  const valueToAngle = (val) => {
    const safe = clamp(val);
    const ratio = (safe - min) / (max - min);
    return Math.PI * (1 - ratio);
  };

  const polar = (angle) => ({
    x: cx + r * Math.cos(angle),
    y: cy - r * Math.sin(angle),
  });

  const arcPath = (a1, a2) => {
    const s = polar(a1);
    const e = polar(a2);
    return `M ${s.x} ${s.y} A ${r} ${r} 0 0 1 ${e.x} ${e.y}`;
  };

  const safeAnimated = clamp(animatedValue);
  const needleAngle = valueToAngle(safeAnimated);
  const needle = polar(needleAngle);

  const safeValue = clamp(value);

  const current =
    segments.find((s) => safeValue >= s.from && safeValue < s.to) ||
    segments[segments.length - 1];

  return (
    <div style={{ width, position: "relative" }}>
      <svg width={width} height={height} style={{ overflow: "visible" }}>
        
        {/* SEGMENTS */}
        {segments.map((seg, i) => {
          const a1 = valueToAngle(seg.from);
          const a2 = valueToAngle(seg.to);

          return (
            <path
              key={i}
              d={arcPath(a1, a2)}
              stroke={seg.color}
              strokeWidth="10"
              fill="none"
              strokeLinecap="round"
              opacity={activeSegment === i ? 1 : 0.4}
              onMouseEnter={() => setActiveSegment(i)}
              onMouseLeave={() => setActiveSegment(null)}
            />
          );
        })}

        {/* NEEDLE */}
        <line
          x1={cx}
          y1={cy}
          x2={needle.x}
          y2={needle.y}
          stroke={current.color}
          strokeWidth="3"
        />

        {/* CENTER DOT */}
        <circle cx={cx} cy={cy} r="4" fill="#111" />
      </svg>

      {/* VALUE */}
      <div style={{ textAlign: "center", marginTop: -2 }}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>
          {safeValue.toFixed(1)}
        </div>
      </div>

      {/* TOOLTIP */}
      {activeSegment !== null && (() => {
        const seg = segments[activeSegment];
        const mid = (seg.from + seg.to) / 2;
        const pos = polar(valueToAngle(mid));

        return (
          <div
            style={{
              position: "absolute",
              left: pos.x,
              top: pos.y - 20,
              transform: "translate(-50%, -100%)",
              background: "#111",
              color: "#fff",
              padding: "4px 8px",
              fontSize: 11,
              borderRadius: 6,
              pointerEvents: "none",
            }}
          >
            {seg.label}
          </div>
        );
      })()}
    </div>
  );
}